import { makeAutoObservable } from 'mobx'
import { t } from '@store/I18nStore'

/** Transfer request lifetime per API (seconds). Keep in sync with operatorTransferNotifications. */
const TRANSFER_PENDING_TTL_SEC = 60

function defaultTransferExpiresAt(fromMs: number = Date.now()): string {
  return new Date(fromMs + TRANSFER_PENDING_TTL_SEC * 1000).toISOString()
}

export type IncomingTransferRequest = {
  requestId: number
  chatId: number
  chatTitle: string
  fromOperatorId: number
  fromOperatorName: string
  expiresAt: string
  text: string
  /** Client clock when the request was queued (for TTL fallback). */
  createdAtMs?: number
}

export type OutgoingTransferPending = {
  requestId: number
  chatId: number
  chatTitle: string
  toOperatorId: number
  toOperatorName: string
  expiresAt: string
  text: string
  /** Client clock when pending was set — used so a past API expiresAt cannot unlock instantly. */
  createdAtMs: number
}

/**
 * Global store for dialog-transfer state:
 * - incoming requests → accept/decline modal on any page
 * - outgoing pending → "waiting for response" banner in the sender's chat
 */
export class OperatorTransfersStore {
  incoming: IncomingTransferRequest[] = []
  outgoingByChatId: Record<string, OutgoingTransferPending> = {}
  actionInFlight: number | null = null
  /** Chat that should be opened in t('chat.tabs.mine') after the operator accepted a transfer. */
  pendingOpenChatId: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  addIncoming(req: IncomingTransferRequest): boolean {
    if (!req.requestId) return false
    if (this.incoming.some((r) => r.requestId === req.requestId)) return false
    this.incoming = [
      ...this.incoming,
      {
        ...req,
        expiresAt: ensureFutureExpiresAt(req.expiresAt),
        createdAtMs: req.createdAtMs ?? Date.now(),
      },
    ]
    return true
  }

  removeByRequestId(requestId: number) {
    if (!Number.isFinite(requestId)) return
    this.incoming = this.incoming.filter((r) => r.requestId !== requestId)
    if (this.actionInFlight === requestId) this.actionInFlight = null
  }

  removeByChatId(chatId: string) {
    if (!chatId) return
    this.incoming = this.incoming.filter((r) => String(r.chatId) !== String(chatId))
  }

  setActionInFlight(requestId: number | null) {
    this.actionInFlight = requestId
  }

  setPendingOpenChatId(chatId: string | null) {
    this.pendingOpenChatId = chatId
  }

  setOutgoingPending(
    chatId: string,
    pending: Omit<OutgoingTransferPending, 'createdAtMs'> & { createdAtMs?: number },
  ) {
    const key = String(chatId || '').trim()
    if (!key) return
    this.outgoingByChatId = {
      ...this.outgoingByChatId,
      [key]: {
        ...pending,
        chatId: Number.isFinite(pending.chatId) ? pending.chatId : Number(key),
        expiresAt: ensureFutureExpiresAt(pending.expiresAt),
        createdAtMs: pending.createdAtMs ?? Date.now(),
      },
    }
  }

  clearOutgoingPending(chatId: string) {
    const key = String(chatId || '').trim()
    if (!key || !this.outgoingByChatId[key]) return
    const next = { ...this.outgoingByChatId }
    delete next[key]
    this.outgoingByChatId = next
  }

  clearOutgoingByRequestId(requestId: number) {
    if (!Number.isFinite(requestId) || requestId <= 0) return
    const entry = Object.entries(this.outgoingByChatId).find(([, p]) => p.requestId === requestId)
    if (entry) this.clearOutgoingPending(entry[0])
  }

  hasOutgoingPending(chatId: string | null | undefined): boolean {
    if (chatId == null || chatId === '') return false
    return Boolean(this.outgoingByChatId[String(chatId)])
  }

  /** Drop incoming/outgoing transfers whose client TTL has elapsed. */
  clearExpired(now: number = Date.now()) {
    const stillValidIncoming = this.incoming.filter(
      (r) => !isClientTransferExpired(r.expiresAt, r.createdAtMs, now),
    )
    if (stillValidIncoming.length !== this.incoming.length) {
      this.incoming = stillValidIncoming
      if (
        this.actionInFlight != null &&
        !stillValidIncoming.some((r) => r.requestId === this.actionInFlight)
      ) {
        this.actionInFlight = null
      }
    }

    let outgoingChanged = false
    const nextOutgoing: Record<string, OutgoingTransferPending> = {}
    for (const [chatId, pending] of Object.entries(this.outgoingByChatId)) {
      if (isClientTransferExpired(pending.expiresAt, pending.createdAtMs, now)) {
        outgoingChanged = true
        continue
      }
      nextOutgoing[chatId] = pending
    }
    if (outgoingChanged) this.outgoingByChatId = nextOutgoing
  }

  reset() {
    this.incoming = []
    this.outgoingByChatId = {}
    this.actionInFlight = null
    this.pendingOpenChatId = null
  }
}

/** Coerce backend expiresAt into a timestamp that is still in the future. */
export function ensureFutureExpiresAt(raw: unknown, fromMs: number = Date.now()): string {
  const s = String(raw ?? '').trim()
  if (s) {
    const t = new Date(s).getTime()
    if (Number.isFinite(t) && t > fromMs + 1000) {
      return new Date(t).toISOString()
    }
  }
  return defaultTransferExpiresAt(fromMs)
}

function isClientTransferExpired(
  expiresAt: string,
  createdAtMs: number | undefined,
  now: number,
): boolean {
  const created = createdAtMs != null && Number.isFinite(createdAtMs) ? createdAtMs : now
  // Never expire sooner than the client TTL from when we queued the request,
  // even if the API sent a past/garbage expiresAt.
  const minAliveUntil = created + TRANSFER_PENDING_TTL_SEC * 1000
  const expiresMs = expiresAt ? new Date(expiresAt).getTime() : NaN
  const deadline = Number.isFinite(expiresMs) ? Math.max(expiresMs, minAliveUntil) : minAliveUntil
  return now >= deadline
}
